import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { nip19 } from 'nostr-tools';
import { useSeoMeta } from '@unhead/react';
import { History, PenSquare, WifiOff } from 'lucide-react';
import { Layout } from '@/components/Layout';
import { ContentBody } from '@/components/ContentBody';
import { ContentFormatSelect } from '@/components/ContentFormatSelect';
import { ScrollableToolbarRow } from '@/components/ScrollableToolbarRow';
import { StarButton } from '@/components/StarButton';
import { ZapButton } from '@/components/ZapButton';
import { ShareDialog } from '@/components/ShareDialog';
import { CommentsDialog } from '@/components/CommentsDialog';
import { HighlightToggle } from '@/components/HighlightToggle';
import { HighlightLayer } from '@/components/HighlightLayer';
import { ReaderModeToggle } from '@/components/ReaderModeToggle';
import { ReaderModeView } from '@/components/ReaderModeView';
import { ExportMenu } from '@/components/ExportMenu';
import { EventDetailsDialog } from '@/components/EventDetailsDialog';
import { OfflineSaveButton } from '@/components/OfflineSaveButton';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAuthor } from '@/hooks/useAuthor';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useContentFormat } from '@/hooks/useContentFormat';
import { useWikiArticle, useWikiRedirect, useWikiVersions } from '@/hooks/useWikiArticles';
import { useOfflineArticle } from '@/hooks/useOfflineLibrary';
import { normalizeWikiTitle, prettifySlug } from '@/lib/wikiSlug';
import { articleTitle, getTagValue, WIKI_ARTICLE_KIND } from '@/lib/nip54';
import NotFound from '@/pages/NotFound';

export function ArticlePage() {
  const { slug: rawSlug, npub } = useParams<{ slug: string; npub?: string }>();
  const navigate = useNavigate();
  const { user } = useCurrentUser();
  const [highlightMode, setHighlightMode] = useState(false);
  const [readerMode, setReaderMode] = useState(false);

  const slug = rawSlug ? normalizeWikiTitle(decodeURIComponent(rawSlug)) : undefined;

  let requestedPubkey: string | undefined;
  if (npub) {
    try {
      const decoded = nip19.decode(npub);
      if (decoded.type === 'npub') requestedPubkey = decoded.data;
    } catch {
      requestedPubkey = undefined;
    }
  }

  const { data: versions, isLoading: versionsLoading } = useWikiVersions(slug);
  const { data: redirect } = useWikiRedirect(!versions?.length ? slug : undefined);

  // Pick the version to display: the one requested via npub, or the newest.
  const selectedPubkey = requestedPubkey ?? versions?.[0]?.pubkey;
  const { data: explicitArticle, isLoading: explicitLoading } = useWikiArticle(
    requestedPubkey ? slug : undefined,
    requestedPubkey,
  );

  const fetched = requestedPubkey ? explicitArticle : versions?.[0];
  const fetchLoading = requestedPubkey ? explicitLoading : versionsLoading;

  // Fall back to the locally-saved copy when the relays don't have (or
  // can't be reached for) this article — the entire point of the
  // offline library. The fetched copy still wins when available, so a
  // saved article never masks a newer published edit.
  const offlineArticle = useOfflineArticle(slug, requestedPubkey);
  const article = fetched ?? offlineArticle;
  const isOffline = !fetched && Boolean(offlineArticle);

  // Never show a loading skeleton over content we already have locally.
  const isLoading = fetchLoading && !offlineArticle;

  const author = useAuthor(article?.pubkey);
  const displayName = author.data?.metadata?.name ?? (article ? `${article.pubkey.slice(0, 8)}…` : '');

  const { detected, selected, setSelected } = useContentFormat(article?.content ?? '');

  useSeoMeta({
    title: article ? `${articleTitle(article)} — Wikistar` : 'Wikistar',
    description: article ? (getTagValue(article, 'summary') ?? article.content.slice(0, 160)) : undefined,
  });

  // Follow redirects (NIP-54 kind 30819) when no article exists under
  // this slug. Skipped when we have a local copy to show — redirecting
  // away from an article the user deliberately saved would be worse
  // than ignoring the redirect, especially while offline.
  useEffect(() => {
    if (offlineArticle) return;
    if (!versionsLoading && versions?.length === 0 && redirect && slug) {
      const targetNpub = nip19.npubEncode(redirect.pubkey);
      navigate(`/w/${encodeURIComponent(redirect.identifier)}/${targetNpub}`, { replace: true });
    }
  }, [versionsLoading, versions, redirect, slug, navigate, offlineArticle]);

  if (!slug) return <NotFound />;

  const canEdit = Boolean(user);
  const isOwnVersion = Boolean(user && article && user.pubkey === article.pubkey);

  return (
    <Layout>
      <div className="mx-auto max-w-3xl px-4 py-8">
        {isLoading ? (
          <ArticleSkeleton />
        ) : !article ? (
          <EmptyArticle slug={slug} canEdit={canEdit} />
        ) : (
          <>
            {/* The action bar is one scrollable row, so all the buttons
                fit on a single line with chevrons at each end when
                there's overflow — instead of wrapping into a tall,
                unruly stack on small screens. */}
            <div className="mb-6 border-b border-border pb-4">
              <div className="mb-3 flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <h1 className="text-3xl font-bold tracking-tight break-words">{articleTitle(article)}</h1>
                  {isOffline && (
                    <Badge variant="secondary" className="mt-2 gap-1.5">
                      <WifiOff className="size-3" />
                      Offline copy
                    </Badge>
                  )}
                </div>
                {/* Format/detected-vs-actual content type sits at the title
                    level (not the action bar) since it's a document-level
                    control rather than a formatting action. */}
                <ContentFormatSelect value={selected} detected={detected} onChange={setSelected} />
              </div>

              <Link
                to={`/${nip19.npubEncode(article.pubkey)}`}
                className="mb-4 flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
              >
                <Avatar className="size-5">
                  <AvatarImage src={author.data?.metadata?.picture} alt={displayName} />
                  <AvatarFallback>{displayName.charAt(0).toUpperCase()}</AvatarFallback>
                </Avatar>
                <span>
                  {isOwnVersion ? 'Your version' : `by ${displayName}`} ·{' '}
                  {new Date(article.created_at * 1000).toLocaleDateString()}
                </span>
              </Link>

              <ScrollableToolbarRow className="rounded-md border border-input bg-muted/40 p-1">
                <span className="inline-flex min-w-0 items-center gap-2">
                  <StarButton
                    event={article}
                    addressPointer={`${WIKI_ARTICLE_KIND}:${article.pubkey}:${slug}`}
                  />
                  <ZapButton event={article} />
                  <CommentsDialog event={article} title={articleTitle(article)} />
                  <HighlightToggle
                    event={article}
                    addressPointer={`${WIKI_ARTICLE_KIND}:${article.pubkey}:${slug}`}
                    enabled={highlightMode}
                    onToggle={setHighlightMode}
                  />
                  {versions && versions.length > 1 ? (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="sm">
                          <History className="size-4" />
                          History
                          <Badge variant="secondary" className="ml-0.5 px-1.5">
                            {versions.length}
                          </Badge>
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-64">
                        {versions.map((v) => (
                          <VersionMenuItem
                            key={v.pubkey}
                            pubkey={v.pubkey}
                            slug={slug}
                            active={v.pubkey === selectedPubkey}
                          />
                        ))}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem asChild>
                          <Link to={`/w/${encodeURIComponent(slug)}/history`}>
                            <History className="size-4" />
                            Full history
                          </Link>
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  ) : (
                    <Button variant="outline" size="sm" asChild>
                      <Link to={`/w/${encodeURIComponent(slug)}/history`}>
                        <History className="size-4" />
                        History
                      </Link>
                    </Button>
                  )}
                  {canEdit && (
                    <Button size="sm" asChild>
                      <Link to={`/w/${encodeURIComponent(slug)}/edit`}>
                        <PenSquare className="size-4" />
                        {isOwnVersion ? 'Edit' : 'Fork & edit'}
                      </Link>
                    </Button>
                  )}
                  <ReaderModeToggle enabled={readerMode} onToggle={setReaderMode} />
                  <OfflineSaveButton event={article} />
                  <ExportMenu event={article} title={articleTitle(article)} format={selected} />
                  <ShareDialog event={article} title={articleTitle(article)} />
                  <EventDetailsDialog event={article} title={articleTitle(article)} />
                </span>
              </ScrollableToolbarRow>
            </div>

            <article>
              <HighlightLayer
                event={article}
                addressPointer={`${WIKI_ARTICLE_KIND}:${article.pubkey}:${slug}`}
                active={highlightMode}
              >
                <ContentBody event={article} format={selected} className="prose-wiki" />
              </HighlightLayer>
            </article>

            {readerMode && (
              <ReaderModeView title={articleTitle(article)} onClose={() => setReaderMode(false)}>
                <ContentBody event={article} format={selected} />
              </ReaderModeView>
            )}
          </>
        )}
      </div>
    </Layout>
  );
}

function VersionMenuItem({ pubkey, slug, active }: { pubkey: string; slug: string; active: boolean }) {
  const author = useAuthor(pubkey);
  const displayName = author.data?.metadata?.name ?? `${pubkey.slice(0, 8)}…`;
  const npub = nip19.npubEncode(pubkey);

  return (
    <DropdownMenuItem asChild className={active ? 'bg-accent' : undefined}>
      <Link to={`/w/${encodeURIComponent(slug)}/${npub}`} className="flex items-center gap-2">
        <Avatar className="size-5">
          <AvatarImage src={author.data?.metadata?.picture} alt={displayName} />
          <AvatarFallback>{displayName.charAt(0).toUpperCase()}</AvatarFallback>
        </Avatar>
        <span className="truncate">{displayName}</span>
      </Link>
    </DropdownMenuItem>
  );
}

function ArticleSkeleton() {
  return (
    <div className="space-y-4">
      <Skeleton className="h-9 w-2/3" />
      <Skeleton className="h-4 w-40" />
      <div className="space-y-3 pt-4">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-5/6" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-3/4" />
      </div>
    </div>
  );
}

function EmptyArticle({ slug, canEdit }: { slug: string; canEdit: boolean }) {
  return (
    <div className="text-center">
      <h1 className="mb-2 text-3xl font-bold tracking-tight break-words">{prettifySlug(slug)}</h1>
      <Card className="mt-6 border-dashed">
        <CardContent className="py-12 px-8 text-center">
          <p className="mx-auto max-w-sm text-muted-foreground">
            There's no article for "{prettifySlug(slug)}" yet.{' '}
            {canEdit ? 'Be the first to write one!' : 'Log in to write one.'}
          </p>
          {canEdit && (
            <Button className="mt-4" asChild>
              <Link to={`/w/${encodeURIComponent(slug)}/edit`}>
                <PenSquare className="size-4" />
                Create this article
              </Link>
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
